import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AccidentRoleType } from '../../enums/accident-role-type';
import { MultiplePartyInvolvementType } from '../../enums/externals-involved-type';
import { InvolvedVehicleType } from '../../enums/involved-vehicle-type';
import { NavigationKey } from '../../enums/navigation-key-type';
import { ClaimsService } from '../claims.service';
import { VerificationService } from '../verification.service';


type NavigatorMap = {
  [key in NavigationKey]?: (value?: any) => NavigationKey;
};

@Injectable({
  providedIn: 'root',
})
export class FormNavigationService {
  private claimsService = inject(ClaimsService);
  private verificationService = inject(VerificationService);

  constructor(private router: Router) {}

  private navigateTo(key: NavigationKey): void {
    const route = key;
    if (!route) {
      console.error(`No route mapping found for key: ${key}`);
      return;
    }
    this.router.navigate([`/${route}`]);
  }

  public formNavigator: NavigatorMap = {
    [NavigationKey['accident-location']]: () => {
      return NavigationKey['damage-type'];
    },
    [NavigationKey['accident-report-finished']]: () => {
      if (this.claimsService.isDriver()) {
        return NavigationKey['ride-reason'];
      } else {
        return NavigationKey['active-ride'];
      }
    },
    [NavigationKey['accident-role']]: (role) => {
      if (role === AccidentRoleType.Pedestrian) {
        return NavigationKey['detail-accident-description'];
      } else {
        return NavigationKey['vehicle-involved'];
      }
    },
    [NavigationKey['active-ride']]: () => {
      return NavigationKey['alcohol-consumption'];
    },
    [NavigationKey['alcohol-consumption']]: () => {
      return NavigationKey['police-report'];
    },
    [NavigationKey['car-parts-involved']]: () => {
      return NavigationKey['incident-description'];
    },
    [NavigationKey['contact-validation']]: () => {
      return NavigationKey['finish'];
    },
    [NavigationKey['damage-type']]: () => {
      if (this.claimsService.isDriver()) {
        return NavigationKey['vehicle-involved'];
      } else if (this.claimsService.isDamagedPerson()) {
        return NavigationKey['accident-role'];
      }
      return NavigationKey['damage-type'];
    },
    [NavigationKey['data-is-correct']]: () => {
      return NavigationKey['overview'];
    },
    [NavigationKey['detail-accident-description']]: () => {
      return NavigationKey['accident-report-finished'];
    },
    [NavigationKey['document-upload-overview']]: () => {
      if (this.claimsService.claimReport().policeReportWasIssued) {
        return NavigationKey['document-upload-police-report'];
      } else {
        return NavigationKey['document-upload-finished'];
      }
    },
    [NavigationKey['document-upload-police-report']]: () => {
      return NavigationKey['document-upload-finished'];
    },
    [NavigationKey['driver-age']]: () => {
      return NavigationKey['alcohol-consumption'];
    },
    [NavigationKey['enter-license-plate']]: () => {
      return NavigationKey['accident-location'];
    },
    [NavigationKey['fault-allocation']]: () => {
      return NavigationKey['third-party-information'];
    },
    [NavigationKey['incident-description']]: () => {
      return NavigationKey['detail-accident-description'];
    },
    [NavigationKey['multiple-party-involvement']]: (involvement) => {
      if (involvement === MultiplePartyInvolvementType.OnlyMe) {
        return NavigationKey['no-processing-possible'];
      } else {
        return NavigationKey['enter-license-plate'];
      }
    },
    [NavigationKey['overview']]: () => {
      if (this.claimsService.claimReport()?.isVerified) {
        return NavigationKey['finish'];
      } else {
        this.verificationService.sendVerificationCode();
        return NavigationKey['contact-validation'];
      }
    },
    [NavigationKey['personal-information']]: () => {
      return NavigationKey['personal-information-finished'];
    },
    [NavigationKey['police-drug-test']]: () => {
      return NavigationKey['witness-details'];
    },
    [NavigationKey['police-report']]: () => {
      return NavigationKey['police-drug-test'];
    },
    [NavigationKey['potential-technical-defect']]: () => {
      return NavigationKey['who-is-to-blame'];
    },
    [NavigationKey['ride-reason']]: () => {
      return NavigationKey['driver-age'];
    },
    [NavigationKey['third-party-information']]: () => {
      return NavigationKey['ride-information-finish'];
    },
    [NavigationKey['user-participation']]: (isDriver) => {
      if (isDriver === true) {
        return NavigationKey['multiple-party-involvement'];
      } else if (isDriver === false) {
        return NavigationKey['enter-license-plate'];
      }
      return NavigationKey['user-participation'];
    },
    [NavigationKey['vehicle-accident-status']]: () => {
      const claimReport = this.claimsService.claimReport();
      if (
        claimReport?.vehicleType === InvolvedVehicleType.FourWheelsWithEngine
      ) {
        return NavigationKey['car-parts-involved'];
      } else {
        return NavigationKey['incident-description'];
      }
    },
    [NavigationKey['vehicle-involved']]: () => {
      const claimReport = this.claimsService.claimReport();
      if (claimReport.vehicleType === InvolvedVehicleType.None) {
        return NavigationKey['incident-description'];
      } else {
        return NavigationKey['vehicle-accident-status'];
      }
    },
    [NavigationKey['witness-information']]: () => {
      if (this.claimsService.isDriver()) {
        return NavigationKey['technical-issue'];
      } else if (this.claimsService.isDamagedPerson()) {
        return NavigationKey['who-is-to-blame'];
      }
      return NavigationKey['witness-information'];
    },
  };

  // public navigate('multiple-party-involvement', [rawValue]);
  public navigate(route: NavigationKey, params?: [any]) {
    const nextRoute = this.formNavigator[route]?.(...(params || []));
    if (nextRoute) {
      this.navigateTo(nextRoute);
    }
  }
}
